import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ConsultationTypeEnum } from '@/shared/enums';
import { useEffect } from 'react';
import {
    bookingDraftAtom,
    emptyUid,
    preferredCaregiverUidAtom,
    selectedTimeSlotDateAtom,
} from '@/features/booking/shared/model/bookingState';
import { fetchAvailableCaregivers } from '@/features/booking/widgets/select-caregiver/api/fetchAvailableCaregivers';
import { fetchPreviouslySelectedCaregiverUid } from '@/features/booking/widgets/select-caregiver/api/fetchPreviouslySelectedCaregiverUid';

type usePreferredCaregiverParams = {
    consultationType?: ConsultationTypeEnum;
    performInitialFetch?: boolean;
};

export const usePreferredCaregiver = ({
    consultationType,
    performInitialFetch = true,
}: usePreferredCaregiverParams) => {
    const draftState = useAtomValue(bookingDraftAtom);
    const [preferredCaregiverUid, setPreferredCaregiverUid] = useAtom(
        preferredCaregiverUidAtom,
    );
    const setSelectedTimeslotDate = useSetAtom(selectedTimeSlotDateAtom);

    const hasPreferredCaregiver = !!preferredCaregiverUid;

    const {
        data: previouslySelectedCaregiverUid,
        isLoading: isFetchingPreviousCaregiver,
        error: previousCaregiverError,
    } = useQuery({
        queryKey: ['previously-selected-caregiver', consultationType],
        queryFn: () => fetchPreviouslySelectedCaregiverUid(consultationType),
        enabled:
            !!consultationType &&
            performInitialFetch &&
            !!draftState &&
            !hasPreferredCaregiver,
        staleTime: 1000 * 60 * 2, // 2min
    });

    const {
        data: caregivers = [],
        isLoading: isFetchingAvailableCaregivers,
        error: caregiversError,
        refetch,
    } = useQuery({
        queryKey: ['available-caregivers', consultationType],
        queryFn: () => fetchAvailableCaregivers(consultationType),
        enabled: !!consultationType && performInitialFetch,
        staleTime: 1000 * 60 * 2, // 2min
    });

    const chooseCaregiver = (uid: string, resetTimeslot = false) => {
        setPreferredCaregiverUid(uid);
        if (resetTimeslot) {
            setSelectedTimeslotDate(null);
        }
    };

    useEffect(() => {
        if (previouslySelectedCaregiverUid && !hasPreferredCaregiver) {
            chooseCaregiver(previouslySelectedCaregiverUid);
            return;
        }

        if (caregivers.length === 1) {
            chooseCaregiver(caregivers[0].Uid);
        }
    }, [previouslySelectedCaregiverUid, caregivers]);

    const safeCaregivers = Array.isArray(caregivers) ? caregivers : [];

    return {
        preferredCaregiverUid: preferredCaregiverUid ?? emptyUid,
        caregivers: safeCaregivers,
        selectedCaregiver: safeCaregivers.find(
            (x) => x.Uid === preferredCaregiverUid,
        ),
        isFetchingPreviousCaregiver,
        isLoading: isFetchingAvailableCaregivers || isFetchingPreviousCaregiver,
        error: caregiversError || previousCaregiverError,
        refetch,
        chooseCaregiver,
        hasPreferredCaregiver,
    };
};
